import { useEffect, useRef } from "react";
import { CheckCircle } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const steps = [
  {
    number: "01",
    title: "Arrecadação",
    description:
      "O importador paga a taxa AFRMM pelo Sistema Mercante antes de iniciar a operação de descarregamento no porto brasileiro.",
    highlighted: false,
    siatechNote: null,
  },
  {
    number: "02",
    title: "Destinação ao FMM",
    description:
      "A Receita Federal viabiliza o recolhimento do valor, que é destinado ao Fundo da Marinha Mercante.",
    highlighted: false,
    siatechNote: null,
  },
  {
    number: "03",
    title: "Solicitação de Creditamento",
    description:
      "EBNs que operam em transporte remunerado por frete — seja em Navegação Interior no Norte e Nordeste ou em Navegação de Cabotagem na costa brasileira — podem solicitar o creditamento dos recursos em suas contas vinculadas. Para isso, precisam cumprir as exigências legais e normativas.",
    highlighted: true,
    siatechNote:
      "É aqui que a maioria das empresas trava — e é aqui que a SIATECH entra.",
  },
  {
    number: "04",
    title: "Requerimento de Liberação",
    description:
      "Após o creditamento, a empresa requer a liberação dos recursos para investir em sua frota, com aval do BNDES.",
    highlighted: false,
    siatechNote: "A SIATECH também apoia nesta etapa — estruturando e acompanhando o requerimento de liberação junto ao BNDES.",
  },
  {
    number: "05",
    title: "Utilização dos Recursos",
    description:
      "Com o parecer favorável do BNDES, através de duplo comando, o recurso é retirado da conta vinculada para utilização exclusiva ao que foi proposto no requerimento.",
    highlighted: false,
    siatechNote: null,
  },
];

const HowItWorks = () => {
  const sectionRef = useScrollReveal();
  const lineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const line = lineRef.current;
    if (!line) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          line.classList.add("timeline-line");
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(line);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="como-funciona"
      ref={sectionRef as React.RefObject<HTMLElement>}
      className="py-24 relative overflow-hidden bg-dots"
      style={{ background: "var(--color-branco-vela)" }}
    >
      <div className="container mx-auto px-4 lg:px-6 relative">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16" data-animate>
          <div className="eyebrow justify-center">O Processo</div>
          <h2
            className="heading-h2 mb-4"
            style={{ color: "var(--color-preto-marca)" }}
          >
            Entenda como funciona o AFRMM — e onde a SIATECH entra.
          </h2>
          <div
            className="w-14 h-px mx-auto mb-6"
            style={{ background: "var(--color-cinza-nevoa)" }}
          />
          <p
            className="text-base font-light leading-relaxed max-w-2xl mx-auto"
            style={{ color: "var(--color-cinza-ancora)" }}
          >
            Os recursos do AFRMM vêm do Fundo da Marinha Mercante (FMM) e podem
            ser utilizados por EBNs para construção, manutenção de ativos ou
            amortização de financiamentos na construção naval.
          </p>
        </div>

        {/* Timeline vertical */}
        <div className="max-w-2xl mx-auto">
          <div className="relative">
            {/* Linha vertical — anima de cima para baixo ao entrar na viewport */}
            <div
              ref={lineRef}
              className="absolute left-[19px] top-0 bottom-0 w-px"
              style={{ background: "var(--color-cinza-nevoa)", opacity: 0.4 }}
            />

            <div className="space-y-0">
              {steps.map((step, index) => (
                <div
                  key={index}
                  data-animate
                  data-animate-delay={String(Math.min(index + 1, 4))}
                  className="relative flex gap-8"
                  style={{ paddingBottom: index < steps.length - 1 ? "2.5rem" : "0" }}
                >
                  {/* Dot + número */}
                  <div className="relative flex-shrink-0 flex flex-col items-center" style={{ width: 40 }}>
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center relative z-10 transition-all duration-300"
                      style={
                        step.highlighted
                          ? {
                              background: "var(--color-preto-marca)",
                              border: "2px solid var(--color-dourado)",
                            }
                          : {
                              background: "var(--color-branco-vela)",
                              border: "1px solid var(--color-cinza-nevoa)",
                            }
                      }
                    >
                      <span
                        className="font-bold text-[11px] tracking-wider"
                        style={{
                          color: step.highlighted
                            ? "var(--color-dourado)"
                            : "var(--color-cinza-ancora)",
                          fontFamily: "var(--font-display)",
                          letterSpacing: "0.08em",
                        }}
                      >
                        {step.number}
                      </span>
                    </div>
                  </div>

                  {/* Conteúdo */}
                  <div className="flex-1 pb-1">
                    {step.highlighted ? (
                      /* Card destacado — passo 03 */
                      <div
                        className="rounded-md p-6"
                        style={{
                          background: "var(--color-preto-marca)",
                          borderTop: "2px solid var(--color-dourado)",
                          border: "1px solid rgba(200, 169, 110, 0.25)",
                        }}
                      >
                        {/* Badge */}
                        <div className="flex items-center gap-2 mb-3">
                          <span
                            className="text-[10px] font-semibold px-2 py-0.5 rounded uppercase tracking-wider"
                            style={{
                              background: "var(--color-dourado)",
                              color: "var(--color-preto-marca)",
                            }}
                          >
                            A SIATECH atua aqui
                          </span>
                        </div>

                        <h3
                          className="font-bold text-lg mb-3 tracking-tight"
                          style={{ color: "var(--color-branco-vela)" }}
                        >
                          {step.title}
                        </h3>
                        <p
                          className="text-[14px] font-light leading-relaxed mb-4"
                          style={{ color: "var(--color-cinza-nevoa)", lineHeight: 1.7 }}
                        >
                          {step.description}
                        </p>

                        {step.siatechNote && (
                          <p
                            className="text-sm font-semibold"
                            style={{ color: "var(--color-dourado)" }}
                          >
                            {step.siatechNote}
                          </p>
                        )}

                        {/* Checklist */}
                        <div className="flex flex-wrap gap-4 mt-5 pt-5"
                          style={{ borderTop: "1px solid rgba(176,175,172,0.12)" }}>
                          {["Sem erros humanos", "Rastreabilidade total", "Sua equipe no estratégico"].map(
                            (item) => (
                              <div key={item} className="flex items-center gap-1.5">
                                <CheckCircle
                                  className="w-3.5 h-3.5 flex-shrink-0"
                                  style={{ color: "#22C55E" }}
                                />
                                <span
                                  className="text-xs font-light"
                                  style={{ color: "var(--color-cinza-nevoa)" }}
                                >
                                  {item}
                                </span>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    ) : (
                      /* Card padrão */
                      <div className="pt-1.5">
                        <h3
                          className="font-semibold text-base mb-2 tracking-tight"
                          style={{ color: "var(--color-preto-marca)" }}
                        >
                          {step.title}
                        </h3>
                        <p
                          className="text-[14px] font-light leading-relaxed"
                          style={{ color: "var(--color-cinza-ancora)", lineHeight: 1.7 }}
                        >
                          {step.description}
                        </p>
                        {step.siatechNote && (
                          <p
                            className="text-[13px] font-medium mt-3 flex items-center gap-1.5"
                            style={{ color: "var(--color-dourado)" }}
                          >
                            <span style={{ fontSize: "10px" }}>◆</span>
                            {step.siatechNote}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA final */}
        <div
          className="max-w-2xl mx-auto mt-14 p-6 rounded-md flex flex-col sm:flex-row items-start sm:items-center gap-5"
          data-animate
          style={{
            background: "var(--color-preto-marca)",
            borderTop: "1px solid var(--color-dourado)",
            border: "1px solid rgba(200, 169, 110, 0.2)",
          }}
        >
          <div className="flex-1">
            <p
              className="text-sm font-light leading-relaxed"
              style={{ color: "var(--color-cinza-nevoa)" }}
            >
              O creditamento é onde o processo trava por erros, burocracia e
              falta de conhecimento. Nossa tecnologia e equipe existem para
              resolver exatamente isso.
            </p>
          </div>
          <a href="#contato" className="btn-cta-dark text-sm whitespace-nowrap flex-shrink-0">
            Análise Gratuita
          </a>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
